import { Unstable_Popup as Popup, useAutocomplete } from '@mui/base';
import cn from 'classnames';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { useElementWidth } from '../../../utils/hooks';
import { Button } from '../../buttons';
import { InputTag, TagGroup, Typography } from '../../data-display';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '../../foundations';
import { Listbox, ListboxItem } from '../../internal';
import { ListboxGroup } from '../../lists/listbox-group';
import { ListboxGroupLabel } from '../../lists/listbox-group-label';
import { BaseInput } from '../base-input';
import { Checkbox } from '../checkbox';

import { splitByDelimiter, tokenizeText } from './autocomplete.utils';

import type { ForwardedRef, ReactElement, SyntheticEvent } from 'react';

import type { BaseInputProps } from '../base-input';
import type {
  AutocompleteComponentType,
  AutocompleteGroupedOption,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
} from './autocomplete.types';

import './autocomplete.scss';

// @ts-expect-error `getOptionLabel` has `option` from `AutocompleteProps`
function defaultGetOptionLabel(option): string {
  return option;
}

function defaultGetLimitTagsText(more: number): string {
  return `+${more}`;
}

function defaultRenderGroup({ children, group, key }: AutocompleteRenderGroupParams): ReactElement {
  return (
    <ListboxGroup key={key}>
      <ListboxGroupLabel>{group}</ListboxGroupLabel>
      {children}
    </ListboxGroup>
  );
}

const Autocomplete = forwardRef(
  <AutocompleteOption extends NonNullable<unknown>, Multiple extends boolean = false, FreeSolo extends boolean = false>(
    props: AutocompleteProps<AutocompleteOption, Multiple, FreeSolo>,
    forwardedRef: ForwardedRef<HTMLElement>,
  ): ReactElement => {
    const {
      autoSelect = false,
      className,
      clearable = false,
      closeOnSelect,
      defaultValue,
      disabled = false,
      filterOptions,
      freeSolo = false,
      getLimitTagsText = defaultGetLimitTagsText,
      getOptionDisabled,
      getOptionLabel = defaultGetOptionLabel,
      groupBy,
      hideSelectedOptions = false,
      id,
      inputValue,
      leftElement,
      limitTags = -1,
      loading = false,
      loadingText = 'Loading...',
      maxRows,
      multiple,
      noOptionsText = 'No options',
      onChange,
      onClose,
      onInputChange,
      onOpen,
      onPaste,
      options,
      pasteDelimiter,
      placeholder,
      readOnly,
      renderOption,
      required,
      rightElement,
      selectAll = false,
      selectAllLabel = 'Select all',
      value: valueProp,
      width,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    // Track the input value separately for multiple mode to preserve search term after selection
    const [internalInputValue, setInternalInputValue] = useState<string>('');
    const previousInputRef = useRef<string>('');
    const isControlledInputValue = inputValue !== undefined;
    const isControlledValue = valueProp !== undefined;

    // For multiple mode, always control the input value to preserve search term
    const shouldControlInput = multiple && !isControlledInputValue;
    const controlledInputValue = shouldControlInput ? internalInputValue : inputValue;

    // When selectAll is enabled and value is uncontrolled, we must internally manage value
    // so that handleSelectAllChange can update the hook's state directly.
    type ValueType = AutocompleteOption | AutocompleteOption[] | null | undefined;
    const shouldControlValue = selectAll && multiple && !isControlledValue;
    const initialValue = (defaultValue ?? (shouldControlValue ? [] : undefined)) as ValueType;
    const [internalValue, setInternalValue] = useState<ValueType>(initialValue);

    const handleChange = useCallback(
      (event: SyntheticEvent, newValue: unknown): void => {
        if (shouldControlValue) {
          setInternalValue(newValue as ValueType);
        }
        onChange?.(event, newValue as never);
      },
      [shouldControlValue, onChange],
    );

    // Handle input change to preserve search term in multiple mode
    const handleInputChange = (event: SyntheticEvent, value: string, reason?: string): void => {
      const allowedReasonsForMultipleModeToPreserveInputValue = ['selectOption', 'reset', 'removeOption'];
      const shouldPreserveInputValue =
        multiple && reason && allowedReasonsForMultipleModeToPreserveInputValue.includes(reason) && shouldControlInput;

      if (shouldPreserveInputValue) {
        // Use the previous input value that was stored before the reset
        const valueToKeep = previousInputRef.current;
        // IMPORTANT: Actually update the internal state to the preserved value
        // This will flow back into the MUI hook through the controlled inputValue prop
        setInternalInputValue(valueToKeep);
        onInputChange?.(event, valueToKeep, reason);
        return;
      }

      // Store the current value before updating (for use when reset happens)
      if (reason === 'input') {
        previousInputRef.current = value;
      }

      // For all other cases, update the state normally
      if (shouldControlInput) {
        setInternalInputValue(value);
      }

      onInputChange?.(event, value, reason);
    };

    const hookValue = (shouldControlValue ? internalValue : valueProp) as typeof valueProp;

    const {
      anchorEl,
      getClearProps,
      getInputProps,
      getListboxProps,
      getOptionProps,
      getPopupIndicatorProps,
      getRootProps,
      getTagProps,
      groupedOptions,
      popupOpen: open,
      setAnchorEl,
      value,
    } = useAutocomplete<AutocompleteOption, Multiple, undefined, typeof freeSolo>({
      autoSelect,
      defaultValue: shouldControlValue ? undefined : defaultValue,
      disableCloseOnSelect: typeof closeOnSelect === 'boolean' ? !closeOnSelect : multiple,
      disabled,
      filterOptions,
      filterSelectedOptions: hideSelectedOptions,
      freeSolo,
      getOptionDisabled,
      getOptionLabel,
      groupBy,
      inputValue: controlledInputValue,
      multiple,
      onChange: handleChange as typeof onChange,
      onClose: () => onClose?.(),
      onInputChange: handleInputChange,
      onOpen: () => onOpen?.(),
      options,
      readOnly,
      value: hookValue,
    });

    const showSelectAll = selectAll && multiple && groupedOptions.length > 0;

    const visibleOptions = useMemo<AutocompleteOption[]>(() => {
      if (!groupBy) {
        return groupedOptions as AutocompleteOption[];
      }
      return (groupedOptions as AutocompleteGroupedOption<AutocompleteOption>[]).flatMap((group) => group.options);
    }, [groupedOptions, groupBy]);

    const enabledVisibleOptions = useMemo(
      () => visibleOptions.filter((opt) => !getOptionDisabled?.(opt)),
      [visibleOptions, getOptionDisabled],
    );

    const selectedValues = useMemo(() => (Array.isArray(value) ? value : []) as AutocompleteOption[], [value]);

    const enabledVisibleSelectedCount = useMemo(
      () => enabledVisibleOptions.filter((opt) => selectedValues.includes(opt)).length,
      [enabledVisibleOptions, selectedValues],
    );

    const allEnabledSelected =
      enabledVisibleOptions.length > 0 && enabledVisibleSelectedCount === enabledVisibleOptions.length;
    const someEnabledSelected = enabledVisibleSelectedCount > 0 && !allEnabledSelected;

    const handleSelectAllClick = useCallback(
      (event: React.MouseEvent<HTMLLIElement>): void => {
        event.preventDefault();
        let newValue: AutocompleteOption[];

        if (allEnabledSelected) {
          const enabledVisibleSet = new Set(enabledVisibleOptions);
          newValue = selectedValues.filter((v) => !enabledVisibleSet.has(v));
        } else {
          const newOptions = enabledVisibleOptions.filter((opt) => !selectedValues.includes(opt));
          newValue = [...selectedValues, ...newOptions];
        }

        if (shouldControlValue) {
          setInternalValue(newValue);
        }
        onChange?.(event as unknown as SyntheticEvent, newValue as never);
      },
      [allEnabledSelected, enabledVisibleOptions, selectedValues, shouldControlValue, onChange],
    );

    /**
     * Processes tokenized values and triggers onChange if there are new values.
     */
    const processTokenizedValues = useCallback(
      (newValues: AutocompleteOption[], event: SyntheticEvent): void => {
        if (newValues.length === 0) {
          return;
        }

        const currentValues = (Array.isArray(value) ? value : []) as AutocompleteOption[];
        const combinedValues = [...currentValues, ...newValues];

        // Clear the input value
        if (shouldControlInput) {
          setInternalInputValue('');
          previousInputRef.current = '';
        }

        // Trigger onChange with the new values
        onChange?.(event, combinedValues as AutocompleteOption[] as never);
      },
      [value, shouldControlInput, onChange],
    );

    /**
     * Handles paste event to parse and add multiple values based on delimiter.
     * Also calls the onPaste callback if provided.
     */
    const handlePaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>): void => {
        const pastedText = event.clipboardData.getData('text');
        const parsedValues = splitByDelimiter(pastedText, pasteDelimiter);

        // Always call the onPaste callback if provided
        onPaste?.(event, { parsedValues, pastedText });

        // Only handle paste tokenization when multiple mode is enabled and delimiter is configured
        if (!multiple || !pasteDelimiter) {
          return;
        }

        const currentValues = (Array.isArray(value) ? value : []) as AutocompleteOption[];
        const newValues = tokenizeText({
          currentValues,
          delimiter: pasteDelimiter,
          freeSolo,
          getOptionDisabled,
          getOptionLabel,
          options,
          text: pastedText,
        });

        // If no new values (single value or all duplicates), let default behavior handle it
        if (newValues.length === 0) {
          return;
        }

        // Prevent default paste behavior since we're handling it
        event.preventDefault();
        processTokenizedValues(newValues, event as unknown as SyntheticEvent);
      },
      [
        multiple,
        onPaste,
        pasteDelimiter,
        value,
        options,
        freeSolo,
        getOptionLabel,
        getOptionDisabled,
        processTokenizedValues,
      ],
    );

    /**
     * Handles keydown event to parse typed text with delimiters when Enter is pressed.
     * Creates tokens similar to paste behavior.
     */
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>): void => {
        // Only handle Enter key
        if (event.key !== 'Enter') {
          return;
        }

        // Only handle when multiple mode is enabled and delimiter is configured
        if (!multiple || !pasteDelimiter) {
          return;
        }

        const inputElement = event.target as HTMLInputElement;
        const inputText = inputElement.value;

        // If empty, let the default behavior handle it
        if (!inputText.trim()) {
          return;
        }

        const currentValues = (Array.isArray(value) ? value : []) as AutocompleteOption[];
        const newValues = tokenizeText({
          currentValues,
          delimiter: pasteDelimiter,
          freeSolo,
          getOptionDisabled,
          getOptionLabel,
          options,
          text: inputText,
        });

        // If no new values (single value or all duplicates), let default behavior handle it
        if (newValues.length === 0) {
          return;
        }

        // Prevent default Enter behavior since we're handling it
        event.preventDefault();
        event.stopPropagation();
        processTokenizedValues(newValues, event as unknown as SyntheticEvent);
      },
      [multiple, pasteDelimiter, value, options, freeSolo, getOptionLabel, getOptionDisabled, processTokenizedValues],
    );

    const hasLimitTags = limitTags >= 0;
    const openPopup = freeSolo ? groupedOptions.length > 0 && open : open;

    const renderTags: BaseInputProps['renderInput'] = (inProps, inRef): ReactElement => {
      const tags = (Array.isArray(value) ? value : []) as AutocompleteOption[];
      const shouldLimit = hasLimitTags && !openPopup;
      const visibleTags = shouldLimit ? tags.slice(0, limitTags) : tags;
      const hiddenCount = tags.length - visibleTags.length;

      const tagGroupStyle =
        maxRows !== undefined && !openPopup
          ? ({ '--_autocomplete-max-rows': maxRows } as React.CSSProperties)
          : undefined;

      return (
        <TagGroup
          className={cn('rbui-autocomplete-tag-group', {
            'rbui-autocomplete-tag-group--scrollable': maxRows !== undefined && !openPopup,
          })}
          style={tagGroupStyle}
        >
          {visibleTags.map((v, index) => {
            const { onDelete, ...tagProps } = getTagProps({ index });

            const key = `${getOptionLabel(v)}-${index}`;

            return (
              <InputTag
                {...tagProps}
                disabled={disabled}
                dismissible={!readOnly}
                key={key}
                onDismiss={onDelete}
                readOnly={readOnly}
                selected
              >
                {getOptionLabel(v)}
              </InputTag>
            );
          })}
          {hiddenCount > 0 && (
            <Typography as="span" className="rbui-autocomplete-limit-text" color="body" variant="text">
              {getLimitTagsText(hiddenCount)}
            </Typography>
          )}
          <input {...inProps} onKeyDown={handleKeyDown} onPaste={handlePaste} ref={inRef} />
        </TagGroup>
      );
    };

    const defaultRenderListOption = (option: AutocompleteOption, index: number): ReactElement => {
      const optionProps = getOptionProps({ index, option });
      const optionDisabled = !!optionProps['aria-disabled'];
      const optionSelected = !!optionProps['aria-selected'];
      const optionLabel = renderOption ? renderOption(option) : getOptionLabel(option);
      const checkType = multiple ? 'checkbox' : 'checkmark';

      return (
        <ListboxItem
          checkType={checkType}
          className={cn('rbui-autocomplete-option', {
            'rbui-autocomplete-option--disabled': optionDisabled,
          })}
          disabled={optionDisabled}
          selected={optionSelected}
          {...optionProps}
          key={optionProps.key}
        >
          {optionLabel}
        </ListboxItem>
      );
    };

    const getClearButton = (): ReactElement => {
      const hasValue = multiple && Array.isArray(value) ? value.length > 0 : !!value;

      const hideClear = disabled || readOnly || !hasValue;

      const { color, tabIndex, ...buttonProps } = getClearProps();

      return (
        <Button
          aria-label="Clear"
          className={cn('rbui-base-input__clear-button', {
            'rbui-base-input__clear-button--hidden': hideClear,
          })}
          startIcon={<CloseIcon />}
          tabIndex={0}
          type="tertiary"
          {...buttonProps}
        />
      );
    };

    const Chevron = open ? ChevronUpIcon : ChevronDownIcon;

    const { ref: inputRef, ...inputProps } = getInputProps();
    const rootRef = mergeRefs([forwardedRef, setAnchorEl]);
    const rootProps = getRootProps();
    const popupWidth = useElementWidth(anchorEl);

    return (
      <>
        {/* @ts-expect-error `rootProps` has `onFocus`, `onBlur` etc from `HTMLAttributes<HTMLDivElement>` */}
        <BaseInput
          className={cn('rbui-autocomplete', className)}
          disabled={disabled}
          id={id}
          inputProps={{
            ...inputProps,
            className: cn({ 'rbui-autocomplete-input': multiple }),
            onPaste: multiple ? undefined : handlePaste,
          }}
          inputRef={inputRef}
          leftElement={leftElement}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={rootRef}
          renderInput={multiple ? renderTags : undefined}
          required={required}
          rightElement={
            <>
              {rightElement}
              {clearable && getClearButton()}
              {!freeSolo && (
                <Chevron
                  aria-description="Toggle popup"
                  aria-hidden
                  className="rbui-autocomplete-chevron"
                  // @ts-expect-error `onClick` has `event` from `React.MouseEventHandler<HTMLButtonElement>`
                  onClick={!(disabled || readOnly) && getPopupIndicatorProps().onClick}
                />
              )}
            </>
          }
          style={width !== undefined ? { width } : undefined}
          {...rootProps}
          {...rest}
        />
        <Popup
          anchor={anchorEl}
          className="rbui-autocomplete-popup"
          offset={0}
          open={openPopup}
          placement="bottom-start"
          role={undefined}
          style={{ minWidth: popupWidth }}
        >
          <Listbox className="rbui-autocomplete-listbox" {...getListboxProps()}>
            {showSelectAll && (
              <>
                <ListboxItem
                  className="rbui-autocomplete-select-all"
                  onClick={handleSelectAllClick}
                  role="option"
                  selected={allEnabledSelected}
                >
                  <Checkbox
                    checked={allEnabledSelected}
                    className="rbui-listbox-item__checkbox"
                    indeterminate={someEnabledSelected}
                  />
                  <span className="rbui-autocomplete-select-all__label">{selectAllLabel}</span>
                  <span className="rbui-autocomplete-select-all__count">({selectedValues.length} selected)</span>
                </ListboxItem>
                <li className="rbui-autocomplete-select-all-divider" />
              </>
            )}
            {groupedOptions.map((option, index) => {
              if (groupBy) {
                const groupOption = option as AutocompleteGroupedOption<AutocompleteOption>;

                return defaultRenderGroup({
                  ...groupOption,
                  children: groupOption.options.map((opt, idx) =>
                    defaultRenderListOption(opt, idx + groupOption.index),
                  ),
                });
              }

              return defaultRenderListOption(option as AutocompleteOption, index);
            })}

            {groupedOptions.length === 0 && !loading && (
              <ListboxItem
                className="rbui-autocomplete-option rbui-autocomplete-option--no-option"
                disabled
                key="no-options"
              >
                {noOptionsText}
              </ListboxItem>
            )}

            {loading && (
              <ListboxItem
                className="rbui-autocomplete-option rbui-autocomplete-option--loading"
                disabled
                key="loading"
              >
                {loadingText}
              </ListboxItem>
            )}
          </Listbox>
        </Popup>
      </>
    );
  },
);

Autocomplete.displayName = 'Autocomplete';

/**
 * Autocomplete allows a user to search for value(s) to select from a list.
 *
 * ### Usage
 *
 * ```tsx
 * import { Autocomplete } from '@bloomreach/react-banana-ui';
 *
 * export default function MyAutocomplete() {
 *   return (
 *     <Autocomplete
 *       options={['Banana', 'Apple', 'Orange', 'Pineapple', 'Strawberry', 'Grape', 'Watermelon']}
 *       placeholder="Select a fruit"
 *       onChange={(_, newValue) => console.log(newValue)}
 *     />
 *   );
 * }
 * ```
 */
export default Autocomplete as AutocompleteComponentType;
